import { useCallback } from 'react'

import mixpanel from 'mixpanel-browser'

import { getStoredUser } from 'auth/infra/StoredUser'
import { MIXPANEL_TOKEN } from 'core/infra/environment'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'

import { AnalyticsEvent, AnalyticsProperties } from './events'

type AnalyticsUser = {
  email: string
  client: string
  firstName: string
  lastName: string
  issuer: string
}

export function useAnalytics() {
  const init = useCallback(() => {
    mixpanel.init(MIXPANEL_TOKEN)
  }, [])

  const setup = useCallback((user: AnalyticsUser) => {
    mixpanel.register({
      ...user,
      app: 'ls-analytics-web',
    })
    mixpanel.people.set(user)
    mixpanel.identify(user.email)
  }, [])

  // Identify the logged-in user once per login. Logins navigate in-app without a
  // reload, so this also runs from track() and not only when the app mounts.
  const identifyStoredUser = useCallback(() => {
    const user = getStoredUser(new LocalStorageClient())
    if (!user?.email || mixpanel.get_distinct_id() === user.email) {
      return
    }

    setup({
      email: user.email,
      client: user.client,
      firstName: user.first_name,
      lastName: user.last_name,
      issuer: user.issuer,
    })
  }, [setup])

  const track = useCallback((event: AnalyticsEvent, properties?: AnalyticsProperties) => {
    try {
      // Ensure Mixpanel is initialized before tracking
      // Mixpanel's init is idempotent, so calling it multiple times is safe
      if (!mixpanel.has_opted_out_tracking && !mixpanel.get_distinct_id) {
        mixpanel.init(MIXPANEL_TOKEN)
      }
      identifyStoredUser()
      mixpanel.track(event, properties)
    } catch (error) {
      console.error('Failed to track analytics event:', error)
    }
  }, [identifyStoredUser])

  return { init, setup, identifyStoredUser, track }
}

export default useAnalytics
